/**
 * ADR-0074 owner decision 2026-10-09 — self-service CI verification identities
 * (routes/ci-identities.ts). Owner / admin of the workspace for reads and writes (a security
 * setting); the workspace comes from the session. The server derives the exact OIDC subject,
 * workflow_ref and signing identity from the three inputs; the client never sends a pattern.
 */
import type { CiVerificationIdentity } from '@aigovops/core';
import { req } from './api';

const base = (slug: string) => `/api/w/${encodeURIComponent(slug)}/settings/ci-identities`;
export type CiIdentity = CiVerificationIdentity;
export const listCiIdentities = (slug: string) => req<{ identities: CiIdentity[]; audience: string }>(base(slug));
export const createCiIdentity = (slug: string, body: { repository: string; workflow_path: string; ref: string }) =>
  req<{ identity: CiIdentity }>(base(slug), { method: 'POST', body: JSON.stringify(body) });
export const disableCiIdentity = (slug: string, identityId: string, reason: string) =>
  req<{ identity: CiIdentity }>(`${base(slug)}/${encodeURIComponent(identityId)}/disable`, { method: 'POST', body: JSON.stringify({ reason }) });

/** Settings → Security, opened on the CI identities form prefilled from a run that claimed this workflow. */
export function trustWorkflowHref(slug: string, claimed?: { repository: string; workflow_path: string | null; ref: string | null } | null, returnTo?: string): string {
  const q = new URLSearchParams();
  const back = returnTo ? safeReturnTo(slug, returnTo) : null;
  if (claimed?.repository) q.set('ci_repo', claimed.repository);
  if (claimed?.workflow_path) q.set('ci_workflow', claimed.workflow_path);
  if (claimed?.ref) q.set('ci_ref', claimed.ref.replace(/^refs\/heads\//, ''));
  if (back) q.set('return_to', back);
  const qs = q.toString();
  return `/w/${encodeURIComponent(slug)}/app/settings/security${qs ? `?${qs}` : ''}#ci-identities`;
}

/**
 * Round 3 (F-H1): "Back to the target plan" only ever goes to an in-app path of THIS workspace on the
 * same origin (no scheme, no `//host`, no backslashes, no other workspace); anything else is dropped.
 */
export function safeReturnTo(slug: string, raw: string | null | undefined): string | null {
  if (!raw || raw.length > 500 || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  let u: URL;
  try { u = new URL(raw, 'https://app.invalid'); } catch { return null; }
  if (u.origin !== 'https://app.invalid') return null;
  const prefix = `/w/${encodeURIComponent(slug)}/app/`;
  if (!u.pathname.startsWith(prefix) || u.pathname.includes('/../') || /[\u0000-\u001f]/.test(raw)) return null;
  return `${u.pathname}${u.search}`;
}
